import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/** One `departments` row as this view reads it (system/departments, table manufacturing_departments). */
export interface DepartmentRow {
  /** `dept_xxxx` — a string id. Never coerce it to a number. */
  id: string;
  name: string;
  description?: string | null;
  isActive?: boolean | null;
}

export interface DepartmentFormData {
  name: string;
  description: string;
  isActive: boolean;
}

/** `departments.name` is NVARCHAR(255) (departments.schema.ts). */
export const MAX_DEPARTMENT_NAME = 255;
/** `departments.description` is NVARCHAR(1000). */
export const MAX_DEPARTMENT_DESCRIPTION = 1000;

const EMPTY_FORM: DepartmentFormData = {
  name: "",
  description: "",
  isActive: true,
};

/** Asks the ServiceStore to REJECT a refused write instead of resolving undefined. */
const THROWING = { throwOnError: true } as never;

/**
 * Administration → Departments.
 *
 * The page used to be the library's inline-edit DataGrid: a react-virtualized
 * MultiGrid whose column widths are fixed pixels (300 + 370 + 120 = 790px), so
 * a phone saw under half the table, and it had no way to ADD a department at
 * all (walkthrough 5×5 §4.2). This store backs the hand-built replacement —
 * a table on desktop, cards on a phone, and an Add/Edit dialog — the same
 * shape as Roles and User Management.
 *
 * Writes go through the `departments` ServiceStore with a per-call
 * `throwOnError`, so a refusal (the API gates every write behind
 * `requireAdmin()`) rejects and is shown instead of reading as success.
 */
export default class DepartmentsStore {
  rootStore: RootStore;

  formData: DepartmentFormData = { ...EMPTY_FORM };
  editingId: string | null = null;
  saveError: string | null = null;

  /**
   * Why the list could not be loaded, or null after a successful load. The
   * ServiceStore swallows a failed `find`, so without this a failure read as
   * "No departments yet".
   */
  loadError: string | null = null;

  /** Name field interacted with, or a save attempted — only then is its error shown. */
  nameTouched = false;
  isSaving = false;
  /** The row whose Active switch is being saved, so it can disable. */
  togglingId: string | null = null;
  /**
   * Why the last Active switch was refused. Kept apart from `saveError`, which
   * belongs to the dialog (its footer shows the thrown message), so one
   * failure is never shown twice.
   */
  toggleError: string | null = null;
  search = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { departmentsStore?: ServiceStoreType })
        ?.departmentsStore ?? null
    );
  }

  /** Every department the ServiceStore holds, by name, ignoring the search box. */
  /**
   * Back to the constructed state on sign-out (RootStore `purgeUserData`), so
   * nothing of the person who signed out is left for the next one.
   */
  reset() {
    this.formData = { ...EMPTY_FORM };
    this.editingId = null;
    this.saveError = null;
    this.loadError = null;
    this.nameTouched = false;
    this.isSaving = false;
    this.togglingId = null;
    this.toggleError = null;
    this.search = "";
  }

  get allRows(): DepartmentRow[] {
    const rows = (this.service?.entities ?? []) as unknown as DepartmentRow[];
    return [...rows].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }

  get rows(): DepartmentRow[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return this.allRows;
    return this.allRows.filter(
      (d) =>
        (d.name ?? "").toLowerCase().includes(q) ||
        (d.description ?? "").toLowerCase().includes(q)
    );
  }

  get activeCount(): number {
    return this.allRows.filter((d) => d.isActive !== false).length;
  }

  get isLoading(): boolean {
    return this.service?.isFindPending === true;
  }

  get isEditing(): boolean {
    return this.editingId !== null;
  }

  setSearch(value: string) {
    this.search = value;
  }

  setFormField<K extends keyof DepartmentFormData>(
    field: K,
    value: DepartmentFormData[K]
  ) {
    this.formData[field] = value;
    if (field === "name") this.nameTouched = true;
    this.saveError = null;
  }

  touchName() {
    this.nameTouched = true;
  }

  clearToggleError() {
    this.toggleError = null;
  }

  /** Open the dialog empty, for a new department. */
  beginCreate() {
    this.editingId = null;
    this.formData = { ...EMPTY_FORM };
    this.saveError = null;
    this.nameTouched = false;
  }

  /** Open the dialog on an existing department. */
  beginEdit(row: DepartmentRow) {
    this.editingId = row.id;
    this.formData = {
      name: row.name ?? "",
      description: row.description ?? "",
      isActive: row.isActive !== false,
    };
    this.saveError = null;
    this.nameTouched = false;
  }

  get nameError(): string | null {
    const name = this.formData.name.trim();
    if (!name) return "A department needs a name.";
    if (name.length > MAX_DEPARTMENT_NAME) {
      return `Keep the name to ${MAX_DEPARTMENT_NAME} characters or fewer.`;
    }
    // `allRows`, not the search-filtered `rows`: a search that hides the clash
    // must not let the duplicate through. Users carry the department by NAME
    // (`users.department`), so two rows with one name are indistinguishable to
    // everything that reads it.
    const clash = this.allRows.find(
      (d) =>
        d.id !== this.editingId &&
        (d.name ?? "").trim().toLowerCase() === name.toLowerCase()
    );
    if (clash) return `A department named "${name}" already exists.`;
    return null;
  }

  get visibleNameError(): string | null {
    return this.nameTouched ? this.nameError : null;
  }

  get isFormValid(): boolean {
    return this.nameError === null && !this.isSaving;
  }

  /**
   * Create or update. Resolves true on success; on false `saveError` holds
   * the reason and the route's onSubmit keeps the dialog open.
   */
  async save(): Promise<boolean> {
    this.nameTouched = true;
    if (this.nameError !== null) return false;
    const service = this.service;
    if (!service) {
      this.saveError = "The departments service is unavailable.";
      return false;
    }
    this.isSaving = true;
    this.saveError = null;
    const payload = {
      name: this.formData.name.trim(),
      description: this.formData.description.trim() || null,
      isActive: this.formData.isActive,
    };
    const ended = sessionMark(this.rootStore);
    try {
      if (this.editingId) {
        // Department ids are strings ('dept_xxxx') — never coerce to Number.
        await service.patch(String(this.editingId), payload as never, THROWING);
      } else {
        await service.create(payload as never, THROWING);
      }
      await this.load();
      // The save committed; a sign-out during the reload left a reset form
      // that may already be the next person's.
      if (ended()) return true;
      runInAction(() => {
        this.isSaving = false;
        this.editingId = null;
        this.formData = { ...EMPTY_FORM };
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        if (!isSessionEnded(error)) this.saveError = loadErrorMessage(error, "The department could not be saved.");
      });
      return false;
    }
  }

  /**
   * Flip one department's Active flag from the list. A refusal is shown on
   * the page (`toggleError`) and the list re-read, so the switch shows what is
   * actually stored rather than what was clicked.
   */
  async setActive(row: DepartmentRow, isActive: boolean): Promise<boolean> {
    const service = this.service;
    if (!service || this.togglingId) return false;
    this.togglingId = row.id;
    this.toggleError = null;
    try {
      await service.patch(String(row.id), { isActive } as never, THROWING);
      return true;
    } catch (error) {
      // The sign-out ended it: no error, and no re-read — that would run in the
      // NEXT session and park its own failure (401) on the emptied page.
      if (isSessionEnded(error)) return false;
      runInAction(() => {
        this.toggleError = loadErrorMessage(error, "The department could not be saved.");
      });
      await this.load();
      return false;
    } finally {
      runInAction(() => {
        this.togglingId = null;
      });
    }
  }

  /**
   * Load (or reload) the list. Never rejects: a failure lands on `loadError`,
   * which the body renders as an error with Retry.
   */
  async load(): Promise<void> {
    try {
      await findOrThrow(this.service, { query: { $limit: 200 } });
      runInAction(() => {
        this.loadError = null;
      });
    } catch (error) {
      runInAction(() => {
        if (!isSessionEnded(error)) this.loadError = loadErrorMessage(error, "The departments could not be loaded.");
      });
    }
  }
}
